import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../api";
import Shell from "../Shell";
import { authHeaders } from "../api";

function fmt(d) {
  if (!d) return "—";
  const dt = new Date(d);
  if (String(dt) === "Invalid Date") return d;
  const dd = String(dt.getDate()).padStart(2, "0");
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const yyyy = dt.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

export default function BlueBox() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [days, setDays] = useState(30);

  const [rehabFilter, setRehabFilter] = useState(""); // "", "1", "0"

  // Admin-only controls
  const [role, setRole] = useState("staff");
  const isAdmin = role === "admin";

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/api/user/role/", {
          headers: authHeaders(),
        });
        setRole(r.data.role);
      } catch {
        setRole("staff");
      }
    })();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);

    try {
      const params = {
        days: Number(days) || 30,
        q: q || "",
      };

      if (rehabFilter !== "") {
        params.in_rehab = rehabFilter;
      }

      const { data } = await api.get("/api/bluebox/", {
        params,
        headers: authHeaders(),
      });

      setRows(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  }, [days, q, rehabFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const markContacted = async (userId) => {
    try {
      await api.post(
        "/api/bluebox/contact/",
        { user: userId },
        { headers: authHeaders() }
      );

      await load();
    } catch (e) {
      alert("Tókst ekki að merkja haft samband.");
    }
  };

  const toggleActiveStatus = async (user) => {
    const nextActive = !user.is_active;
    const actionLabel = nextActive ? "virkja" : "óvirkja";

    if (!window.confirm(`Ertu viss um að ${actionLabel} þennan notanda?`)) {
      return;
    }

    try {
      await api.post(
        `/api/users/${user.id}/set_active/`,
        { is_active: nextActive },
        { headers: authHeaders() }
      );

      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Tókst ekki að breyta stöðu notanda.");
    }
  };

  const displayRows = useMemo(() => {
    if (!rows.length) return rows;
    if (rows[0]?.__random_top === true) return rows;

    const copy = rows.slice();
    const idx = Math.floor(Math.random() * copy.length);
    const [first] = copy.splice(idx, 1);

    return [first, ...copy];
  }, [rows]);

  return (
    <Shell title="Blái Kassinn" rightMenu>
      <div className="members-toolbar" style={{ marginBottom: 10 }}>
        <div className="search-block">
          <label className="sr" htmlFor="search">
            Leita
          </label>

          <input
            id="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Leita að nafni / notendanafni"
          />
        </div>

        <div
          className="filter-block"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <label htmlFor="days">Dagafjöldi:</label>

          <input
            id="days"
            type="number"
            min={1}
            value={days}
            onChange={(e) => setDays(e.target.value)}
            style={{ width: 90 }}
          />

          <label htmlFor="rehabFilter">Er í endurhæfingu:</label>

          <select
            id="rehabFilter"
            value={rehabFilter}
            onChange={(e) => setRehabFilter(e.target.value)}
          >
            <option value="">Allir</option>
            <option value="1">Já</option>
            <option value="0">Nei</option>
          </select>

          <button className="btn sm" onClick={load}>
            Uppfæra
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading">Hleð…</div>
      ) : (
        <>
          <div className="table-wrap">
            <table className="table zebra">
              <thead>
                <tr>
                  <th>Nafn</th>
                  <th>Notendanafn</th>
                  <th>Sími</th>
                  <th>Netfang</th>
                  <th>Í endurhæfingu</th>
                  <th>Síðast mætt</th>
                  <th>Síðast haft samband</th>
                  <th>Frá</th>
                  <th>Dagar</th>
                  <th>Aðgerðir</th>
                </tr>
              </thead>

              <tbody>
                {displayRows.length === 0 ? (
                  <tr>
                    <td colSpan={10} style={{ textAlign: "center", padding: 16 }}>
                      Enginn í bið – allir innan {days} daga 🎉
                    </td>
                  </tr>
                ) : (
                  displayRows.map((u) => (
                    <tr key={u.id}>
                      <td>{u.full_name}</td>
                      <td>{u.username}</td>
                      <td>{u.phone_number}</td>
                      <td>{u.email}</td>
                      <td>{u.is_in_rehab ? "Já" : "Nei"}</td>
                      <td>{fmt(u.attended_last)}</td>
                      <td>{fmt(u.last_contacted_at)}</td>
                      <td>{fmt(u.anchor_date)}</td>
                      <td>{u.days_since}</td>

                      <td>
                        <div
                          style={{
                            display: "flex",
                            gap: 8,
                            whiteSpace: "nowrap",
                            flexWrap: "wrap",
                          }}
                        >
                          <button className="btn sm" onClick={() => markContacted(u.id)}>
                            Merkja haft samband
                          </button>

                          {isAdmin && (
                            <button
                              className="btn danger sm"
                              onClick={() => toggleActiveStatus(u)}
                            >
                              {u.is_active ? "Óvirkja" : "Virkja"}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div style={{ textAlign: "center", marginTop: 10, opacity: 0.8 }}>
            Samtals í lista: {displayRows.length}
          </div>
        </>
      )}
    </Shell>
  );
}